import { debounce } from 'lodash-es';
import { ViewerEventType, ViewerViewportChangeEvent } from '../../types/ViewerEvents.type';
import { browser } from '../../helpers/BrowserHelper';
import { getViewState, watchScroll } from '../../utils/ui-utils';
import { ThumbnailAssetViewerView } from '../views/AssetViewerView';
import { ViewerLayout } from './ViewerLayout';
const VIEW_LABEL_CLICK_DEBOUNCE_TIMEOUT = 500;
const CONTENT_HEIGHT = 96;
const LABEL_HEIGHT = 40;
const EXPANDED_COVER_VIEW_HEIGHT = 56;
const ENABLE_VIEW_SELECTION_CLASS = 'enable-view-selection';
let checkboxInstance = 0;
export class LayoutVerticalThumbnails extends ViewerLayout {
    constructor(config, eventBus, assetViewerItems, tokenService, linkService) {
        super(config, eventBus, assetViewerItems);
        this.config = config;
        this.eventBus = eventBus;
        this.assetViewerItems = assetViewerItems;
        this.tokenService = tokenService;
        this.linkService = linkService;
        this.container = null;
        this.viewSelectionEnabled = false;
        this.canExpandGroup = true;
        this.canExpandGroup = assetViewerItems.length > 1 ? true : false;
        this.onFocusChange = this.onFocusChange.bind(this);
        this.addViews = this.addViews.bind(this);
        this.views = this.createAssetViewerViewsFromAssetViewerItems(this.assetViewerItems);
        this.addEventListeners();
        this.setCurrentView = debounce(this.setCurrentView.bind(this), 80);
        // TODO destroy Layout and remove this eventListener
        this.linkService.eventBus.on(ViewerEventType.AddViews, this.addViews);
    }
    addEventListeners() {
        super.addEventListeners();
        this.linkService.eventBus.on(ViewerEventType.FocusChange, this.onFocusChange);
    }
    removeEventListeners() {
        super.removeEventListeners();
        this.linkService.eventBus.off(ViewerEventType.FocusChange, this.onFocusChange);
    }
    render(container) {
        this.container = container;
        if (this.viewSelectionEnabled) {
            this.addEnableViewSelectionClass(this.container);
        }
        this.viewportWidth = this.container.clientWidth;
        this.viewportHeight = this.container.clientHeight;
        this.calculateLayout();
        this.container.classList.add('viewer-app-thumbnail-container');
        this.container.style.overflowY = 'scroll';
        this.container.style.transform = 'translateZ(0)';
        this.scrollContainer = this.container;
        this.scroll = watchScroll(this.scrollContainer, this.onScroll.bind(this));
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
        this.renderAll();
        this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this._viewState }));
    }
    renderAll() {
        this.container.innerHTML = '';
        const fragment = document.createDocumentFragment();
        this.views.forEach(view => {
            if (view.parentView && view.parentView.childElement) {
                view.parentView.childElement.appendChild(this.renderView(view));
            }
            else {
                fragment.appendChild(this.renderView(view));
                if (view.childElement) {
                    fragment.appendChild(view.childElement);
                }
            }
        });
        this.container.appendChild(fragment);
        // TODO is there a cleaner way to do this?
        // check if there"s a fixed scrollbar, adjust the viewportWidth and rerender
        // TODO IE doesn"t render labels on rerender???
        if (!browser.ie && !browser.mobile && this.container.clientWidth !== this.container.offsetWidth) {
            this.viewportWidth = this.container.clientWidth;
            this.calculateLayout();
            this.container.innerHTML = '';
            const fragment = document.createDocumentFragment();
            this.views.forEach(view => {
                if (view.parentView && view.parentView.childElement) {
                    view.parentView.childElement.appendChild(this.renderView(view));
                }
                else {
                    fragment.appendChild(this.renderView(view));
                    if (view.childElement) {
                        fragment.appendChild(view.childElement);
                    }
                }
            });
            this.container.appendChild(fragment);
        }
    }
    renderView(view) {
        const isLastSlide = this.views.indexOf(view) === this.views.length - 1;
        const container = view.container;
        const contentWrapper = view.contentWrapper;
        const contentElement = view.contentElement;
        if (!isLastSlide) {
            container.style.borderBottomWidth = view.borderBottom + 'px';
        }
        // contentWrapper.style.height = CONTENT_HEIGHT + (view.paddingTop + view.paddingBottom + view.totalBorderWidth) + "px"
        contentWrapper.style.paddingTop = view.paddingTop + 'px';
        contentWrapper.style.paddingBottom = view.paddingBottom + 'px';
        contentWrapper.style.paddingLeft = view.paddingLeft + 'px';
        contentWrapper.style.paddingRight = view.paddingRight + 'px';
        contentElement.style.width = view.slideWidth + 'px';
        contentElement.style.height = view.slideHeight + 'px';
        contentElement.style.backgroundImage = `url(${view.defaultThumb})`;
        const backgroundPadding = 10;
        const backgroundSize = Math.min(view.slideHeight, view.slideWidth, 50 + backgroundPadding) - backgroundPadding;
        contentElement.style.backgroundSize = `${backgroundSize}px`;
        contentElement.style.backgroundRepeat = 'no-repeat';
        contentElement.style.backgroundPosition = 'center';
        // container.style.top = view.top + "px"
        container.style.height = view.height + 'px';
        // container.style.transform = `translateY(${item.top}px)`;
        if (view.isZoomed) {
            container.scrollLeft = view.scrollXOffset;
        }
        contentWrapper.appendChild(contentElement);
        container.appendChild(contentWrapper);
        const contentLabel = (view.contentLabel = view.contentLabel || createLabel(view));
        if (contentLabel.innerText === '') {
            contentLabel.innerText = view.labelText;
            contentLabel.title = view.labelText;
        }
        contentLabel.style.position = 'absolute';
        contentLabel.style.padding = '5px 8px';
        contentLabel.style.bottom = '0';
        contentLabel.style.whiteSpace = 'nowrap';
        contentLabel.style.textOverflow = 'ellipsis';
        contentLabel.style.overflow = 'hidden';
        contentLabel.style.fontSize = '14px';
        contentLabel.style.borderRadius = '2px';
        contentLabel.style.textAlign = 'center';
        contentLabel.style.touchAction = 'manipulation';
        contentLabel.style.transform = 'translateX(-50%)';
        contentLabel.style.left = '50%';
        if (this.canExpandGroup && view.isCover && view.assetViewerItem.numViews > 1) {
            this.addChevron(view);
        }
        if (!view.isCover && (!view.isPartOfRange || view.isFirstPageOfRange)) {
            const checkbox = view.checkbox;
            const checkboxInput = view.checkboxInput;
            const styledCheckbox = view.styledCheckbox;
            checkboxInput.onclick = () => {
                const checked = checkboxInput.checked;
                if (view.linkedView) {
                    this.toggleView(view, !!checked);
                    if (view.isFirstPageOfRange) {
                        let nextView = this.views[view.page + 1];
                        while (nextView && nextView.isPartOfRange && !nextView.isFirstPageOfRange) {
                            this.toggleView(nextView, checked);
                            nextView = this.views[nextView.page + 1];
                        }
                    }
                }
            };
            checkbox.appendChild(checkboxInput);
            checkbox.appendChild(styledCheckbox);
            container.appendChild(view.checkbox);
        }
        else if (!view.isCover && view.isPartOfRange && !view.isFirstPageOfRange) {
            contentLabel.classList.add('indent-on-selection');
            contentWrapper.classList.add('indent-on-selection');
        }
        container.appendChild(contentLabel);
        return container;
        function createLabel(view) {
            const element = document.createElement('div');
            element.classList.add('thumbnail-label');
            return element;
        }
    }
    toggleView(view, selected) {
        const styledCheckbox = view.styledCheckbox;
        if (styledCheckbox) {
            styledCheckbox.style.background = selected ? this.config.TINT_COLOR : 'white';
        }
        selected ? view.linkedView.selectView() : view.linkedView.deselectView();
    }
    addChevron(view) {
        const chevron = (view.chevron = view.chevron || document.createElement('div'));
        const contentLabel = view.contentLabel;
        chevron.classList.add('chevron-icon');
        chevron.innerHTML = `
            <svg width="1792" height="1792" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M1408 704q0 26-19 45l-448 448q-19 19-45 19t-45-19l-448-448q-19-19-19-45t19-45 45-19h896q26 0 45 19t19 45z"/></svg>
        `;
        contentLabel.style.paddingRight = '30px';
        contentLabel.style.cursor = 'pointer';
        contentLabel.onclick = debounce(event => this.toggleGroup(event, view), VIEW_LABEL_CLICK_DEBOUNCE_TIMEOUT, {
            leading: true,
            trailing: false
        });
        contentLabel.appendChild(chevron);
    }
    toggleGroup(event, view) {
        event.stopPropagation();
        const groupContainer = view.childElement;
        // groupContainer.addEventListener("transitionend", resetHeight.bind(this))
        if (view.isExpanded) {
            view.isExpanded = false;
            groupContainer.style.height = '0px';
            view.container.classList.remove('expanded');
        }
        else {
            view.isExpanded = true;
            groupContainer.style.height = 'auto';
            view.container.classList.add('expanded');
        }
        // TODO after animation
        const resetHeight = () => {
            // groupContainer.removeEventListener("transitionend", resetHeight)
            this.calculateLayout();
            this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
            this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this.viewState }));
        };
        resetHeight();
    }
    update(index = null, adjustScrollPosition = true) {
        this.calculateLayout(index);
        if (index !== null) {
            const view = this.views[index];
            this.renderView(view);
            if (this.scrollYOffset) {
                this.container.scrollTop = this.viewState.top + this.scrollYOffset;
            }
        }
        else {
            this.renderAll();
        }
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
    }
    addViews(event) {
        const { view, numViews } = event.payload;
        if (numViews <= 1) {
            return;
        }
        const index = this.views.findIndex(v => v.linkId === view.id);
        if (index === -1) {
            // TODO why does this happen?
            console.error('view not found in addViews');
            return;
        }
        const thumbnailView = this.views[index];
        const viewsToAdd = [];
        const isExpanded = this.assetViewerItems.length === 1 ? true : false;
        const groupContainer = LayoutVerticalThumbnails.createGroupContainer();
        groupContainer.style.height = isExpanded ? 'auto' : '0px';
        thumbnailView.isCover = true;
        thumbnailView.isExpanded = isExpanded;
        thumbnailView.childElement = groupContainer;
        thumbnailView.container.classList.add('thumbnail-cover');
        if (isExpanded) {
            thumbnailView.container.classList.add('expanded');
        }
        for (let i = 0; i < numViews; i++) {
            const page = i + 1;
            const viewToAdd = this.createAssetViewerViewFromAssetViewerItem(view.assetViewerItem, page);
            // viewToAdd.contentElement.style.width = suggestedWidth + "px";
            // viewToAdd.contentElement.style.height = suggestedHeight + "px";
            viewsToAdd.push(viewToAdd);
        }
        this.views = [...this.views.slice(0, index + 1), ...viewsToAdd, ...this.views.slice(index + 1)];
        if (!thumbnailView.container.parentNode) {
            // thumbnail might not be appended to the DOM yet
            return;
        }
        this.calculateLayout();
        viewsToAdd.forEach(view => groupContainer.appendChild(this.renderView(view)));
        thumbnailView.container.parentNode.insertBefore(groupContainer, thumbnailView.container.nextSibling);
        if (this.canExpandGroup) {
            this.addChevron(thumbnailView);
        }
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
    }
    calculateLayout(changedSlideIndex) {
        let sumOfViewHeights = 0;
        let sumOfGroupHeights = 0;
        let groupTop = 0;
        let previousGroupId = null;
        let lastCoverView = null;
        this.views = this.views.map((view, index) => {
            const isLastSlide = index === this.views.length - 1;
            const labelHeight = LABEL_HEIGHT;
            const borderBottom = isLastSlide || (view.groupId && !view.isLastOfGroup) ? 0 : this.config.VIEW_BORDER_BOTTOM;
            const paddingTop = view.groupId === null || view.isCover ? this.config.VIEW_PADDING_TOP : this.config.PAGE_PADDING_TOP;
            const paddingBottom = view.groupId === null || (view.groupId && view.isLastOfGroup) || view.isCover
                ? this.config.VIEW_PADDING_BOTTOM
                : this.config.PAGE_PADDING_BOTTOM;
            const paddingLeft = this.config.VIEW_PADDING_LEFT;
            const paddingRight = this.config.VIEW_PADDING_RIGHT;
            const totalBorderWidth = this.config.VIEW_BORDER * 2;
            const previousViewState = Object.assign({}, view);
            view.linkedView = this.linkService.viewer
                ? this.linkService.viewer.views.find(parentView => view.linkId === parentView.id)
                : null;
            if (view.dimensions && !view.isZoomed) {
                const maxContentHeight = CONTENT_HEIGHT;
                const widthScale = view.dimensions.width / (this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth));
                const scaledHeight = view.dimensions.height / widthScale;
                if (scaledHeight > maxContentHeight) {
                    const heightScale = view.dimensions.height / maxContentHeight;
                    view.slideWidth = view.dimensions.width / heightScale;
                    view.slideHeight = view.dimensions.height / heightScale;
                }
                else {
                    view.slideWidth = view.dimensions.width / widthScale;
                    view.slideHeight = view.dimensions.height / widthScale;
                }
                view.currentScale = view.slideWidth / view.dimensions.width;
            }
            else if (!view.isZoomed) {
                // if a thumbnail has no dimensions yet, make it square, otherwise the transition from being too wide to smaller is ugly
                view.slideWidth = Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), CONTENT_HEIGHT);
                view.slideHeight = CONTENT_HEIGHT;
            }
            view.width = view.slideWidth + paddingLeft + paddingRight + totalBorderWidth;
            view.height = CONTENT_HEIGHT + (paddingTop + paddingBottom + totalBorderWidth + labelHeight);
            if (view.isCover && view.isExpanded) {
                view.height = EXPANDED_COVER_VIEW_HEIGHT;
            }
            view.top = sumOfViewHeights;
            view.bottom = view.top + view.height;
            view.slideBorder = this.config.VIEW_BORDER;
            view.paddingTop = paddingTop;
            view.paddingBottom = paddingBottom;
            view.paddingLeft = paddingLeft;
            view.paddingRight = paddingRight;
            view.totalBorderWidth = totalBorderWidth;
            view.borderBottom = borderBottom;
            if (view.isCover || view.groupId !== previousGroupId) {
                sumOfGroupHeights = 0;
                groupTop = 0;
            }
            // if (view.groupId !== previousGroupId) {
            //   lastCoverView = null
            // }
            if (view.isCover) {
                lastCoverView = view;
            }
            if (!view.parentView || (lastCoverView && lastCoverView.isExpanded && view.parentView)) {
                sumOfViewHeights = sumOfViewHeights + view.height;
            }
            if (view.parentView) {
                const isFirstOfGroup = view.isFirstOfGroup;
                const isLastOfGroup = this.views[index + 1] && this.views[index + 1].groupId !== view.groupId;
                groupTop = sumOfViewHeights;
                // view.containerTop = groupTop
                // view.containerHeight = sumOfGroupHeights + view.height
                // view.top = sumOfGroupHeights
                // view.bottom = sumOfGroupHeights + view.height
                // view.containerHeight = sumOfGroupHeights + view.height
                sumOfGroupHeights = sumOfGroupHeights + view.height;
                previousGroupId = view.groupId;
                lastCoverView.groupHeight = sumOfGroupHeights;
            }
            // if the current slide height changed, check if the scrollTop should be set, to avoid jumping of the views
            if (index === changedSlideIndex && previousViewState) {
                const oldHeight = previousViewState.height;
                const newHeight = view.height;
                const slideBottomIsAboveViewBottom = previousViewState.bottom < this._viewState.bottom;
                this.scrollYOffset = null;
                if (slideBottomIsAboveViewBottom) {
                    this.scrollYOffset = newHeight - oldHeight;
                }
                // if view is zoomed, center the scroll position
                view.scrollXOffset = view.isZoomed ? (view.width - this.viewportWidth) / 2 : null;
            }
            return view;
        });
        this.layoutHeight = sumOfViewHeights;
    }
    onScroll(event, state) {
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, state);
        if (this._previousViewState &&
            this._previousViewState.first.view === this._viewState.first.view &&
            this._previousViewState.last.view === this._viewState.last.view &&
            this._previousViewState.focus.view === this._viewState.focus.view) {
            return;
        }
        this._previousViewState = this._viewState;
        this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this._viewState }));
    }
    resize() {
        if (this.container) {
            this.viewportWidth = this.container.clientWidth;
            this.viewportHeight = this.container.clientHeight;
            this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
            this.calculateLayout();
        }
    }
    onFocusChange(event) {
        this.setCurrentView(event.payload.assetViewerView);
    }
    // debounced in the constructor to avoid too much flashing when scrolling fast
    setCurrentView(linkedView) {
        // TODO scrollViewIntoView
        const view = this.views.find(view => view.linkId === linkedView.id);
        if (!view) {
            console.error('no view');
            return;
        }
        this.scrollThumbnailIntoView(view);
        this.setSelectedViewStyles(view);
    }
    setSelectedViewStyles(view) {
        const allSelectedItems = document.querySelectorAll('.viewer-app-thumbnail-container .selected');
        if (allSelectedItems && allSelectedItems.length) {
            for (let i = 0; i < allSelectedItems.length; i++) {
                allSelectedItems[i].classList.remove('selected');
            }
        }
        view.container.classList.add('selected');
        if (view.isCover && this.views[this.views.indexOf(view) + 1]) {
            this.views[this.views.indexOf(view) + 1].container.classList.add('selected');
            view.childElement.classList.add('selected');
        }
        if (view.parentView) {
            view.parentView.container.classList.add('selected');
            view.parentView.childElement.classList.add('selected');
        }
    }
    scrollThumbnailIntoView(view) {
        const viewState = this.viewState;
        if (viewState) {
            const visibleThumbs = viewState.views;
            const numVisibleThumbs = visibleThumbs && visibleThumbs.length;
            // If the thumbnail isn"t currently visible, scroll it into view.
            if (numVisibleThumbs > 0) {
                const first = viewState.first.id;
                // Account for only one thumbnail being visible.
                const last = numVisibleThumbs > 1 ? viewState.last.id : first;
                const viewToFocus = view.parentView && !view.parentView.isExpanded ? view.parentView : view;
                const viewId = this.views.indexOf(viewToFocus);
                if (viewId <= first || viewId >= last) {
                    this.scrollContainer.scrollTop = viewToFocus.top;
                }
            }
        }
    }
    enableViewSelection() {
        this.viewSelectionEnabled = true;
        if (this.container) {
            this.addEnableViewSelectionClass(this.container);
        }
    }
    addEnableViewSelectionClass(container) {
        container.classList.add(ENABLE_VIEW_SELECTION_CLASS);
    }
    disableViewSelection() {
        this.viewSelectionEnabled = false;
        if (this.container) {
            this.removeEnableViewSelectionClass(this.container);
        }
    }
    removeEnableViewSelectionClass(container) {
        container.classList.remove(ENABLE_VIEW_SELECTION_CLASS);
    }
    createAssetViewerViewFromAssetViewerItem(assetViewerItem, page) {
        const ensuredPage = page || 1;
        const id = assetViewerItem.id + (page ? '-' + parseInt(page) : '');
        const annotations = (assetViewerItem.annotations && assetViewerItem.annotations[ensuredPage]) || null;
        const thumbnailAssetViewerView = new ThumbnailAssetViewerView(this.config, this.eventBus, this, assetViewerItem, this.tokenService, id, ensuredPage, annotations);
        thumbnailAssetViewerView.linkId = assetViewerItem.id + (page ? '-' + parseInt(page) : '');
        thumbnailAssetViewerView.linkService = this.linkService;
        return thumbnailAssetViewerView;
    }
    createAssetViewerViewsFromAssetViewerItems(assetViewerItems) {
        const views = [];
        assetViewerItems.forEach(assetViewerItem => {
            if (assetViewerItem.numViews > 1) {
                // Create Cover or regular view
                const isExpanded = assetViewerItems.length === 1 ? true : false;
                const thumbnailAssetViewerView = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, 1);
                thumbnailAssetViewerView.childElement = LayoutVerticalThumbnails.createGroupContainer();
                thumbnailAssetViewerView.childElement.style.height = isExpanded ? 'auto' : '0px';
                thumbnailAssetViewerView.isCover = true;
                thumbnailAssetViewerView.isExpanded = isExpanded;
                if (isExpanded) {
                    thumbnailAssetViewerView.container.classList.add('expanded');
                }
                thumbnailAssetViewerView.container.classList.add('thumbnail-cover');
                views.push(thumbnailAssetViewerView);
                let pageGroups = [];
                const { assetViewerItem: { nodeItem: { asset: { lockedPages = [] } = {} } = {} } = {} } = thumbnailAssetViewerView;
                if (this.config.ENABLE_GROUPED_PAGE_SELECTION && lockedPages) {
                    pageGroups = lockedPages.map(range => {
                        const [start, end] = range.split('-');
                        return { start: Number(start), end: Number(end) };
                    });
                }
                for (let i = 0; i < assetViewerItem.numViews; i++) {
                    const page = i + 1;
                    const thumbnailPageAssetViewerView = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, page);
                    thumbnailPageAssetViewerView.parentView = thumbnailAssetViewerView;
                    thumbnailPageAssetViewerView.groupId = assetViewerItem.id;
                    if (i === 0) {
                        thumbnailPageAssetViewerView.isFirstOfGroup = true;
                    }
                    if (i === assetViewerItem.numViews - 1) {
                        thumbnailPageAssetViewerView.isLastOfGroup = true;
                    }
                    pageGroups.forEach(range => {
                        if (page >= range.start && page <= range.end) {
                            thumbnailPageAssetViewerView.isPartOfRange = true;
                            thumbnailPageAssetViewerView.isFirstPageOfRange = page === range.start;
                        }
                    });
                    views.push(thumbnailPageAssetViewerView);
                }
            }
            else if (assetViewerItem.pageRanges && assetViewerItem.pageRanges.length) {
                assetViewerItem.pageRanges.forEach((pageRange, rangeIndex) => {
                    for (let i = pageRange.start; i <= pageRange.end; i++) {
                        let view = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, i);
                        views.push(view);
                    }
                });
            }
            else {
                const thumbnailAssetViewerView = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, assetViewerItem.page);
                views.push(thumbnailAssetViewerView);
            }
        });
        return views;
    }
    createContainer() {
        const element = document.createElement('div');
        element.style.position = 'relative';
        element.style.boxSizing = 'border-box';
        element.style.width = '100%';
        element.classList.add('vrow');
        element.classList.add('thumbnail-container');
        return element;
    }
    createCheckbox() {
        checkboxInstance++;
        const label = document.createElement('label');
        label.classList.add('checkbox');
        // TODO better positioning, without magic numbers (flexbox?)
        label.style.position = 'absolute';
        label.style.left = '14px';
        label.style.top = '44px';
        label.style.zIndex = '10';
        return label;
    }
    createStyledCheckbox() {
        const styledCheckbox = document.createElement('div');
        styledCheckbox.classList.add('checkbox__indicator');
        styledCheckbox.style.width = '23px';
        styledCheckbox.style.height = '23px';
        return styledCheckbox;
    }
    createCheckboxInput() {
        const input = document.createElement('input');
        input.type = 'checkbox';
        return input;
    }
    createContentWrapper() {
        const element = document.createElement('div');
        window._wrapper = element;
        element.style.position = 'absolute';
        element.style.top = '39%';
        element.style.left = '50%';
        element.style.transform = 'translate(-50%,-50%)';
        element.classList.add('vrow-content-container-wrapper');
        return element;
    }
    createContentContainer() {
        const element = document.createElement('div');
        element.classList.add('vrow-content-container');
        element.classList.add('loading');
        element.style.position = 'relative';
        element.style.boxSizing = 'content-box';
        element.style.background = 'white';
        element.style.display = 'flex';
        if (this.config.TRANSITIONS_ENABLED) {
            element.style.transition = 'opacity .5s ease-in-out';
        }
        return element;
    }
    static createGroupContainer() {
        const element = document.createElement('div');
        element.style.overflow = 'hidden';
        element.classList.add('group-container');
        return element;
    }
}
//# sourceMappingURL=LayoutVerticalThumbnails.js.map